import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Heart, ShareIos, ChatBubbleEmpty, MoreHoriz } from "iconoir-react";

import { usePostsStore } from "@/stores/posts";
import {
  PostOptionsSheet,
  PostOwnerOptionsSheet,
  PostShareSheet,
} from "@/pages/feed/components";
import { AuthenticatedLayout } from "@/components/layout/AuthenticatedLayout";
import { mockPosts } from "@/lib/mock-data/posts";
import { PostHeader } from "./components/PostHeader";
import { PostActions } from "./components/PostActions";
import { CommentInput } from "./components/CommentInput";

export function PostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { likePost, unlikePost, deletePost } = usePostsStore();
  const [isLiking, setIsLiking] = useState(false);
  const [showOptionsSheet, setShowOptionsSheet] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [comment, setComment] = useState("");

  // Lấy dữ liệu trực tiếp từ mockPosts thay vì thông qua store
  const [post] = useState(() => {
    let found = mockPosts.find((p) => p.id === id) || null;
    if (!found) {
      // Thử lấy từ localStorage nếu không có trong mockPosts
      const local = JSON.parse(localStorage.getItem("locuno_posts") || "[]");
      found =
        local.find((p: { id: string }) => String(p.id) === String(id)) || null;
      if (found) {
        // Map lại cho đúng format
        return {
          ...found,
          author: {
            id: found.author?.id || "local",
            username: found.author?.username || "ban",
            display_name: "Bạn",
            location: "Local",
            avatar_url: found.author?.avatar_url || "/avatars/kiki.svg",
            is_staff: false,
            is_verified: false,
          },
          media: (() => {
            if (!Array.isArray(found.media)) return [];
            return found.media.map((m) => {
              if (typeof m === "string") {
                const isVideo = /\.(mp4|webm|ogg)$/i.test(m);
                return {
                  url: m,
                  type: isVideo ? ("video" as const) : ("image" as const),
                };
              }
              return m;
            });
          })(),
          category: found.category,
          community: found.community || null,
          type: found.type || "regular",
          title: found.title || null,
          status: found.status || "active",
          location: found.location || null,
          tags: found.tags || [],
          is_featured: found.is_featured || false,
          is_edited: found.is_edited || false,
          edited_at: found.edited_at || null,
          view_count: found.view_count || 0,
          comments_count: found.comments_count || 0,
          reactions_count: found.reactions_count || 0,
          participants_count: found.participants_count || 0,
          is_liked: found.is_liked || false,
          latest_comments: found.latest_comments || [],
          media_preview: found.media_preview || [],
          createdAt: found.createdAt || new Date().toISOString(),
          updated_at: found.updated_at || new Date().toISOString(),
          start_date: found.start_date || null,
          end_date: found.end_date || null,
          max_participants: found.max_participants || null,
          helper: found.helper || null,
          due_date: found.due_date || null,
        };
      }
    }
    return found;
  });

  if (!post) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#FBF6F1]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#215E60]"></div>
      </div>
    );
  }

  const isOwnPost = post.author.display_name === "Bạn";

  const handleLikeClick = async () => {
    if (isLiking) return;

    setIsLiking(true);
    try {
      if (post.is_liked) {
        await unlikePost(post.id);
      } else {
        await likePost(post.id);
      }
    } finally {
      setIsLiking(false);
    }
  };

  const handleShareClick = () => {
    setShowShareSheet(true);
  };

  const handleCommentSubmit = () => {
    if (comment.trim()) {
      // Handle comment submission logic here
      console.log("Submitting comment:", comment);
      setComment("");
    }
  };

  const handleEditPost = () => {
    navigate(`/edit-post/${post.id}`);
    setShowOptionsSheet(false);
  };

  const handleDeletePost = async () => {
    if (
      post.author.display_name === "Bạn" &&
      post.author.location === "Local"
    ) {
      const local = JSON.parse(localStorage.getItem("locuno_posts") || "[]");
      const newLocal = local.filter((p: { id: string }) => p.id !== post.id);
      localStorage.setItem("locuno_posts", JSON.stringify(newLocal));
      navigate(-1);
      return;
    }
    try {
      await deletePost(post.id);
      navigate(-1);
    } catch (error) {
      console.error("Failed to delete post:", error);
    }
  };

  return (
    <AuthenticatedLayout backgroundColor="#FBF6F1" showNavbar={false}>
      {/* Page header */}
      <div className="flex justify-between items-center p-4">
        <button onClick={() => navigate(-1)} className="p-1">
          <ChevronLeft className="w-6 h-6 text-[#1E3A3A]" />
        </button>
        <h1 className="text-xl font-semibold font-manrope text-[#1E3A3A]">
          Chi tiết bài đăng
        </h1>
        <div className="w-6 h-6"></div> {/* Spacer to balance layout */}
      </div>

      {/* Post card */}
      <div className="bg-[#FBF6F1] px-4 py-3">
        {/* Header with user info */}
        <PostHeader
          post={post}
          onOptionsClick={() => setShowOptionsSheet(true)}
        />

        {/* Post content */}
        <div className="pb-2">
          <p className="text-sm text-[#1E3A3A] whitespace-pre-wrap font-sora">
            {post.content}
          </p>
        </div>

        {/* Post media */}
        {(post.media?.length ?? 0) > 0 && (
          <div className="mb-4 relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] max-w-none">
            {post.media![0].type === "image" ? (
              <img
                src={post.media![0].url}
                alt=""
                className="w-screen h-[250px] object-cover"
              />
            ) : (
              <video
                src={post.media![0].url}
                className="w-screen h-[250px] object-cover"
                controls
              />
            )}
          </div>
        )}

        {/* Action buttons */}
        <PostActions
          post={post}
          isLiking={isLiking}
          onLikeClick={handleLikeClick}
          onShareClick={handleShareClick}
        />
      </div>

      {/* Comments section */}
      <div className="px-4 pt-4 pb-24">
        {post.comments_count > 0 ? (
          <div className="space-y-4">
            <div className="flex gap-3">
              <img
                src="/avatars/haku.svg"
                alt="User"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-sm">Haku</span>
                  <div className="w-1 h-1 rounded-full bg-[#6F6F6B]"></div>
                  <span className="text-xs text-[#6F6F6B]">1d</span>
                </div>
                <p className="text-sm text-[#6F6F6B] mt-1">Mình nha bạn ơi</p>
                <div className="flex items-center gap-2 mt-2">
                  <button className="flex items-center gap-1">
                    <Heart
                      className="w-[18px] h-[18px]"
                      stroke="#1E3A3A"
                      strokeWidth={1.2}
                    />
                  </button>

                  <div className="w-2"></div>

                  <button className="flex items-center gap-1">
                    <ChatBubbleEmpty
                      className="w-[18px] h-[18px]"
                      fill="none"
                    />
                    <span className="text-xs text-[#1E3A3A]">Trả lời</span>
                  </button>

                  <div className="w-2"></div>

                  <button className="flex items-center gap-1">
                    <ShareIos
                      className="w-[18px] h-[18px]"
                      stroke="#1E3A3A"
                      strokeWidth={1.2}
                    />
                  </button>

                  <div className="w-2"></div>

                  <button className="flex items-center">
                    <MoreHoriz className="w-[18px] h-[18px]" fill="#1E3A3A" />
                  </button>

                  <div className="flex-1"></div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <img
                src="/avatars/marnie.svg"
                alt="User"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-sm">Marnie</span>
                  <div className="w-1 h-1 rounded-full bg-[#6F6F6B]"></div>
                  <span className="text-xs text-[#6F6F6B]">1d</span>
                </div>
                <p className="text-sm text-[#6F6F6B] mt-1">Bạn ở đâu thế.</p>
                <div className="flex items-center gap-2 mt-2">
                  <button className="flex items-center gap-1">
                    <Heart
                      className="w-[18px] h-[18px]"
                      stroke="#1E3A3A"
                      strokeWidth={1.2}
                    />
                  </button>
                  <div className="w-2"></div> {/* 8px spacing */}
                  <button className="flex items-center gap-1">
                    <ChatBubbleEmpty
                      className="w-[18px] h-[18px]"
                      fill="none"
                    />
                    <span className="text-xs text-[#1E3A3A]">Trả lời</span>
                  </button>
                  <div className="w-2"></div> {/* 8px spacing */}
                  <button className="flex items-center gap-1">
                    <ShareIos
                      className="w-[18px] h-[18px]"
                      stroke="#1E3A3A"
                      strokeWidth={1.2}
                    />
                  </button>
                  <div className="w-2"></div>
                  <button className="flex items-center">
                    <MoreHoriz className="w-[18px] h-[18px]" fill="#1E3A3A" />
                  </button>
                  <div className="flex-1"></div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <img
                src="/avatars/ponyo.svg"
                alt="User"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-sm">Ponyo</span>
                  <div className="w-1 h-1 rounded-full bg-[#6F6F6B]"></div>
                  <span className="text-xs text-[#6F6F6B]">1d</span>
                </div>
                <p className="text-sm text-[#6F6F6B] mt-1">
                  Bạn rep tin nhắn mình nhé
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <button className="flex items-center gap-1">
                    <Heart
                      className="w-[18px] h-[18px]"
                      stroke="#1E3A3A"
                      strokeWidth={1.2}
                    />
                  </button>
                  <div className="w-2"></div> {/* 8px spacing */}
                  <button className="flex items-center gap-1">
                    <ChatBubbleEmpty
                      className="w-[18px] h-[18px]"
                      fill="none"
                    />
                    <span className="text-xs text-[#1E3A3A]">Trả lời</span>
                  </button>
                  <div className="w-2"></div> {/* 8px spacing */}
                  <button className="flex items-center gap-1">
                    <ShareIos
                      className="w-[18px] h-[18px]"
                      stroke="#1E3A3A"
                      strokeWidth={1.2}
                    />
                  </button>
                  <div className="w-2"></div>
                  <button className="flex items-center">
                    <MoreHoriz className="w-[18px] h-[18px]" fill="#1E3A3A" />
                  </button>
                  <div className="flex-1"></div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-[#6F6F6B]">
            <p>Hiện chưa có bình luận nào</p>
          </div>
        )}
      </div>

      {/* Comment input box - Hidden when PostOptionsSheet is open */}
      <CommentInput
        comment={comment}
        onCommentChange={setComment}
        onCommentSubmit={handleCommentSubmit}
        showOptionsSheet={showOptionsSheet}
      />

      {/* Post Options Sheet */}
      {isOwnPost ? (
        <PostOwnerOptionsSheet
          isOpen={showOptionsSheet}
          onClose={() => setShowOptionsSheet(false)}
          onEdit={handleEditPost}
          onDelete={handleDeletePost}
        />
      ) : (
        <PostOptionsSheet
          post={post}
          isOpen={showOptionsSheet}
          onClose={() => setShowOptionsSheet(false)}
        />
      )}

      {/* Post Share Sheet */}
      <PostShareSheet
        post={post}
        isOpen={showShareSheet}
        onClose={() => setShowShareSheet(false)}
      />
    </AuthenticatedLayout>
  );
}
